import { ListItem, ListItemButton, ListItemIcon, ListItemProps, ListItemText, Skeleton } from '@mui/material';
import { getLatestMetaAnalysisResultId, getResultStatus } from 'helpers/MetaAnalysis.helpers';
import { useGetMetaAnalysisResultById } from 'hooks';
import { MetaAnalysisReturn } from 'neurosynth-compose-typescript-sdk';
import useGetMetaAnalysisJobById from 'pages/MetaAnalysis/hooks/useGetMetaAnalysisJobById';
import useGetMetaAnalysisJobsByMetaAnalysisId from 'pages/MetaAnalysis/hooks/useGetMetaAnalysisJobsByMetaAnalysisId';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';

const ProjectsPageCardSummaryMetaAnalysesListItem = (props: ListItemProps & { metaAnalysis: MetaAnalysisReturn }) => {
    const { metaAnalysis, ...listItemProps } = props;
    const latestResultId = getLatestMetaAnalysisResultId(metaAnalysis);

    const { data: metaAnalysisResult, isLoading: getMetaAnalysisResultIsLoading } =
        useGetMetaAnalysisResultById(latestResultId);

    const { data: metaAnalysisJobs, isLoading: metaAnalysisJobsIsLoading } = useGetMetaAnalysisJobsByMetaAnalysisId(
        metaAnalysis?.id
    );
    const jobs = metaAnalysisJobs ?? [];
    const latestJob = jobs.length > 0 ? jobs[jobs.length - 1] : undefined;
    const { data: latestMetaAnalysisJob, isLoading: latestJobIsLoading } = useGetMetaAnalysisJobById(latestJob?.job_id);

    const resultStatus = useMemo(() => {
        return getResultStatus(metaAnalysis, metaAnalysisResult, latestMetaAnalysisJob);
    }, [metaAnalysis, metaAnalysisResult, latestMetaAnalysisJob]);

    if (getMetaAnalysisResultIsLoading || metaAnalysisJobsIsLoading || latestJobIsLoading) {
        return (
            <ListItem {...listItemProps} divider disableGutters disablePadding>
                <ListItemButton>
                    <ListItemIcon>
                        <Skeleton width={40} height={40} variant="circular" />
                    </ListItemIcon>
                    <ListItemText
                        primary={
                            <Skeleton
                                variant="rectangular"
                                width="100%"
                                height="1.5rem"
                                sx={{ marginBottom: '0.25rem' }}
                            />
                        }
                        secondary={<Skeleton variant="rectangular" width="100%" />}
                    />
                </ListItemButton>
            </ListItem>
        );
    }

    return (
        <ListItem divider disableGutters disablePadding {...listItemProps}>
            <ListItemButton
                sx={{
                    padding: '0px',
                    borderRadius: '4px',
                    transition: '200ms ease-in-out',
                    '&:hover': {
                        padding: '0px 16px',
                        transition: '200ms ease-in-out',
                    },
                }}
                component={Link}
                to={`/projects/${metaAnalysis.project}/meta-analyses/${metaAnalysis.id}`}
            >
                <ListItemText
                    sx={{ minHeight: '44px' }}
                    primary={metaAnalysis.name}
                    secondary={resultStatus.statusText}
                    secondaryTypographyProps={{ color: resultStatus.color + '.main' }}
                />
            </ListItemButton>
        </ListItem>
    );
};

export default ProjectsPageCardSummaryMetaAnalysesListItem;
